import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Copy, Trash2, FileText, ClipboardList, Download, Upload, Sparkles, Lock } from "lucide-react";

const FIELD_TYPES = [
  { v: "text", label: "Short text" },
  { v: "long-text", label: "Long text" },
  { v: "email", label: "Email" },
  { v: "phone", label: "Phone" },
  { v: "date", label: "Date" },
  { v: "number", label: "Number" },
  { v: "rating", label: "Rating 0–10 (pain / difficulty scale)" },
  { v: "likert", label: "Likert 0–4 (PDQ-39 style: Never → Always)" },
  { v: "checkbox", label: "Checkbox / consent" },
  { v: "signature", label: "Signature" },
  // File attachment — the client uploads a PDF or image and the
  // operator can view / download the raw file. Optionally wired to
  // an OCR + LLM auto-fill target so the client's uploaded DEXA /
  // referral is parsed into a pre-fill suggestion for a text field.
  { v: "file", label: "File attachment (PDF / image, e.g. DEXA / referral)" },
];

const PREFILL_OPTIONS = [
  { v: "", label: "None" },
  { v: "client_name", label: "Client name (from Acuity)" },
  { v: "client_email", label: "Client email (from Acuity)" },
  { v: "client_phone", label: "Client phone (from Acuity)" },
  { v: "today", label: "Today's date" },
];

function newField() {
  return {
    id: `field_${Math.random().toString(36).slice(2, 8)}`,
    label: "New field",
    type: "text",
    required: false,
    group: "Details",
    prefill: "",
    help: "",
  };
}

export default function OnboardingTemplatesSettings() {
  const { can } = useAuth();
  const editable = can("owner", "manager");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [seeding, setSeeding] = useState(false);

  async function seedV2Defaults() {
    setSeeding(true);
    try {
      const r = await api.post("/intake/templates/seed-v2-defaults?activate=true");
      const created = (r.data?.created || []).length;
      const updated = (r.data?.updated || []).length;
      toast.success("v2.0 forms installed", {
        description: `${created} new · ${updated} updated · PDQ-39 held for licensing.`,
      });
      await load();
    } catch (e) {
      toast.error("Seed failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSeeding(false);
    }
  }

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/intake/templates");
      setRows(r.data || []);
    } catch (e) {
      toast.error("Could not load templates", { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function createBlank(kind = "intake") {
    try {
      const r = await api.post("/intake/templates", {
        name: kind === "questionnaire" ? "New questionnaire" : "New template",
        description: "",
        fields: [],
        kind,
      });
      toast.success(`${kind === "questionnaire" ? "Questionnaire" : "Template"} created`);
      load();
      setEditing(r.data);
    } catch (e) {
      toast.error("Could not create", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function clone(t) {
    try {
      await api.post(`/intake/templates/${t.id}/clone`, { new_name: `${t.name} (copy)` });
      toast.success("Template cloned");
      load();
    } catch (e) {
      toast.error("Could not clone", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function remove(t) {
    if (!window.confirm(`Delete template "${t.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/intake/templates/${t.id}`);
      toast.success("Template deleted");
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Backend returns HTTP 409 + a structured detail listing the
      // onboarding triggers that still reference this template.
      if (e?.response?.status === 409 && detail?.error === "template_in_use") {
        const list = (detail.triggers || [])
          .map((tr) => `• ${tr.name || `Appointment #${tr.appt_type_id}`} (${tr.role})`)
          .join("\n");
        toast.error("Template still in use", {
          description: `${detail.message}\n\n${list}`,
          duration: 10000,
        });
        return;
      }
      toast.error("Could not delete", { description: detail || e.message });
    }
  }

  async function cleanupTestTemplates() {
    try {
      const prev = await api.get("/intake/templates/cleanup-test-preview");
      const count = prev.data?.count || 0;
      if (count === 0) {
        toast.info("No TEST templates found — nothing to clean up.");
        return;
      }
      const sample = (prev.data?.sample_names || []).slice(0, 5).join(", ");
      const extra = count > 5 ? `, and ${count - 5} more` : "";
      if (!window.confirm(
        `Delete ${count} leftover TEST template${count === 1 ? "" : "s"}?\n\n` +
        `Preview: ${sample}${extra}\n\n` +
        `Only rows whose name starts with "TEST " AND have no templateVersion are removed. ` +
        `v2.0 forms are safe.`,
      )) return;
      const r = await api.post("/intake/templates/cleanup-test");
      toast.success(`Removed ${r.data?.deleted || 0} TEST template${r.data?.deleted === 1 ? "" : "s"}`);
      load();
    } catch (e) {
      toast.error("Cleanup failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function exportOne(t) {
    try {
      const r = await api.get(`/intake/templates/${t.id}/export`);
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const slug = (t.name || "template").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      a.href = url;
      a.download = `${slug || "template"}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Template exported", { description: `${t.name} — downloaded as JSON` });
    } catch (e) {
      toast.error("Export failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function handleImportFiles(fileList) {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    let ok = 0;
    let failed = 0;
    for (const f of files) {
      try {
        const text = await f.text();
        const data = JSON.parse(text);
        if (!data || typeof data !== "object" || !data.name || !Array.isArray(data.fields)) {
          throw new Error("File does not look like an exported template");
        }
        await api.post("/intake/templates/import", {
          name: data.name,
          description: data.description || "",
          kind: data.kind || "intake",
          fields: data.fields,
        });
        ok += 1;
      } catch (e) {
        failed += 1;
        toast.error(`Import failed: ${f.name}`, { description: e?.response?.data?.detail || e.message });
      }
    }
    if (ok) toast.success(`${ok} template${ok === 1 ? "" : "s"} imported`);
    if (ok) load();
  }

  return (
    <Card className="p-6 rounded-lg border border-zinc-200 shadow-none" data-testid="onboarding-templates-settings">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
            <ClipboardList className="h-3.5 w-3.5" /> Onboarding templates
          </div>
          <p className="mt-1 text-sm text-zinc-600 max-w-2xl">
            Create and manage intake forms &amp; questionnaires. These templates are what get sent to
            clients from the Onboarding page and from Standalone dispatches.
          </p>
          <div className="mt-1 text-2xs font-semibold uppercase tracking-[0.2em] text-zinc-400">
            {rows.length} template{rows.length === 1 ? "" : "s"}
          </div>
        </div>
        {editable && (
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => createBlank("intake")}
              data-testid="intake-template-new"
              className="bg-zinc-950 hover:bg-zinc-800 text-white"
              size="sm"
            >
              <Plus className="h-4 w-4 mr-1.5" /> New intake form
            </Button>
            <Button
              onClick={() => createBlank("questionnaire")}
              data-testid="intake-questionnaire-new"
              variant="outline"
              className="border-zinc-300 hover:bg-zinc-100"
              size="sm"
            >
              <Plus className="h-4 w-4 mr-1.5" /> New questionnaire
            </Button>
            <label
              className="inline-flex items-center gap-1.5 h-9 px-3 text-sm font-medium rounded-md border border-zinc-300 hover:bg-zinc-100 cursor-pointer"
              data-testid="intake-template-import-label"
            >
              <Upload className="h-4 w-4" /> Import JSON
              <input
                type="file"
                accept="application/json,.json"
                multiple
                className="hidden"
                data-testid="intake-template-import-input"
                onChange={(e) => {
                  handleImportFiles(e.target.files);
                  e.target.value = "";
                }}
              />
            </label>
            <Button
              onClick={seedV2Defaults}
              disabled={seeding}
              variant="outline"
              className="border-indigo-300 text-indigo-800 hover:bg-indigo-50"
              size="sm"
              title="Import the seven bundled v2.0 onboarding forms (Onero, GLA:D, Clinical Pilates/EP, Parkinson's, Neurological, Oncology, PDQ-39). Idempotent — safe to re-run."
              data-testid="intake-template-seed-v2"
            >
              <Sparkles className="h-4 w-4 mr-1.5" />
              {seeding ? "Seeding…" : "Install v2.0 forms"}
            </Button>
            <Button
              onClick={cleanupTestTemplates}
              variant="outline"
              className="border-red-300 text-red-800 hover:bg-red-50"
              size="sm"
              title="Delete every leftover TEST template written by past test runs. v2.0 forms are always safe."
              data-testid="intake-template-cleanup-test"
            >
              <Trash2 className="h-4 w-4 mr-1.5" /> Clean up TEST forms
            </Button>
          </div>
        )}
      </div>

      <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {loading && <div className="text-sm text-zinc-500">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="text-sm text-zinc-500">No templates yet.</div>
        )}
        {rows.map((t) => (
          <Card
            key={t.id}
            className="p-4 border border-zinc-200 hover:border-zinc-300 transition-colors"
            data-testid={`intake-template-${t.id}`}
          >
            <div className="flex items-start gap-2">
              <FileText className="h-4 w-4 text-zinc-500 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-zinc-950 truncate">{t.name}</div>
                {t.description && (
                  <div className="text-xs text-zinc-600 mt-0.5 line-clamp-2">{t.description}</div>
                )}
                <div className="text-xs text-zinc-500 mt-1 flex items-center gap-2 flex-wrap">
                  <span>{(t.fields || []).length} fields</span>
                  <span className={
                    (t.kind || "intake") === "questionnaire"
                      ? "text-2xs uppercase tracking-wider px-1.5 py-0 rounded border border-violet-200 bg-violet-50 text-violet-800"
                      : "text-2xs uppercase tracking-wider px-1.5 py-0 rounded border border-emerald-200 bg-emerald-50 text-emerald-800"
                  }>
                    {(t.kind || "intake") === "questionnaire" ? "questionnaire" : "intake"}
                  </span>
                  {t.templateVersion && (
                    <span
                      className="text-2xs uppercase tracking-wider px-1.5 py-0 rounded border border-indigo-200 bg-indigo-50 text-indigo-800 font-mono-num"
                      title={t.effectiveDate ? `Effective ${t.effectiveDate}` : "v2.0 template"}
                      data-testid={`intake-template-version-${t.id}`}
                    >
                      v{t.templateVersion}
                    </span>
                  )}
                  {t.status && t.status !== "production_ready" && (
                    <span
                      className={
                        "text-2xs uppercase tracking-wider px-1.5 py-0 rounded border inline-flex items-center gap-0.5 " +
                        (t.status.includes("licence")
                          ? "border-amber-300 bg-amber-50 text-amber-900"
                          : "border-zinc-300 bg-zinc-50 text-zinc-700")
                      }
                      title={t.status.replace(/_/g, " ")}
                      data-testid={`intake-template-status-${t.id}`}
                    >
                      {t.status.includes("licence") && <Lock className="h-2.5 w-2.5" />}
                      {t.status.includes("licence") ? "licence gate" : t.status.replace(/_/g, " ")}
                    </span>
                  )}
                  {!t.active && (
                    <span className="text-2xs uppercase tracking-wider px-1.5 py-0 rounded border border-red-200 bg-red-50 text-red-800">
                      inactive
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {editable ? (
                <Button size="sm" variant="outline" onClick={() => setEditing(t)} data-testid={`intake-template-edit-${t.id}`}>Edit</Button>
              ) : (
                <Button size="sm" variant="outline" onClick={() => setEditing(t)}>View</Button>
              )}
              {editable && (
                <>
                  <Button size="sm" variant="outline" onClick={() => clone(t)}><Copy className="h-3.5 w-3.5 mr-1" />Clone</Button>
                  <Button size="sm" variant="outline" onClick={() => exportOne(t)} data-testid={`intake-template-export-${t.id}`}>
                    <Download className="h-3.5 w-3.5 mr-1" />Export
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-700" onClick={() => remove(t)} data-testid={`intake-template-delete-${t.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </>
              )}
            </div>
          </Card>
        ))}
      </div>

      {editing && (
        <TemplateEditor
          template={editing}
          editable={editable}
          onClose={(saved) => { setEditing(null); if (saved) load(); }}
        />
      )}
    </Card>
  );
}

function TemplateEditor({ template, editable, onClose }) {
  const [name, setName] = useState(template.name || "");
  const [description, setDescription] = useState(template.description || "");
  const [fields, setFields] = useState(template.fields || []);
  const [active, setActive] = useState(template.active !== false);
  const [status, setStatus] = useState(template.status || "");
  const [saving, setSaving] = useState(false);

  function update(idx, patch) {
    setFields((arr) => arr.map((f, i) => i === idx ? { ...f, ...patch } : f));
  }
  function addField() { setFields((arr) => [...arr, newField()]); }
  function move(idx, dir) {
    setFields((arr) => {
      const out = [...arr];
      const ni = idx + dir;
      if (ni < 0 || ni >= out.length) return arr;
      [out[idx], out[ni]] = [out[ni], out[idx]];
      return out;
    });
  }
  function remove(idx) {
    setFields((arr) => arr.filter((_, i) => i !== idx));
  }

  async function save() {
    setSaving(true);
    try {
      const cleaned = fields.map((f) => ({
        ...f,
        prefill: f.prefill === "_none" ? "" : f.prefill,
      }));
      const patch = { name, description, fields: cleaned, active };
      // Only send status if it changed / was set — avoids overwriting
      // v2 templates that had never had a status.
      if (status) patch.status = status;
      await api.patch(`/intake/templates/${template.id}`, patch);
      toast.success("Template saved");
      onClose(true);
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(false); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="intake-template-editor">
        <DialogHeader>
          <DialogTitle>{editable ? "Edit template" : "View template"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} disabled={!editable} className="mt-1.5" data-testid="template-name-input" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} disabled={!editable} className="mt-1.5" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3 p-3 rounded border border-zinc-200 bg-zinc-50">
            <div className="flex items-start gap-3">
              <Switch
                checked={active}
                onCheckedChange={setActive}
                disabled={!editable}
                data-testid="template-active-switch"
              />
              <div>
                <div className="text-sm font-medium text-zinc-950">Active</div>
                <div className="text-xs text-zinc-600">
                  Off = hidden from every "Intake form" dropdown and blocked from being sent.
                  Past submissions still render fine because they carry a snapshot.
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs">Lifecycle status</Label>
              <Select value={status || "_unset"} onValueChange={(v) => setStatus(v === "_unset" ? "" : v)} disabled={!editable}>
                <SelectTrigger className="mt-1.5" data-testid="template-status-select">
                  <SelectValue placeholder="Not set" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_unset">Not set</SelectItem>
                  <SelectItem value="production_ready">Production ready</SelectItem>
                  <SelectItem value="draft_clinical_review">Draft — clinical review</SelectItem>
                  <SelectItem value="draft_pending_licence_and_clinical_approval">Licence gate — pending licence &amp; approval</SelectItem>
                  <SelectItem value="retired">Retired</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-2xs text-zinc-500 mt-1">
                Set to <b>Production ready</b> to clear the licence-gate badge (e.g. once you hold the PDQ-39 licence).
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Fields ({fields.length})</Label>
              {editable && (
                <Button size="sm" variant="outline" onClick={addField} data-testid="template-add-field">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add field
                </Button>
              )}
            </div>
            <div className="space-y-2">
              {fields.map((f, idx) => (
                <Card key={idx} className="p-3 border border-zinc-200">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Field label</Label>
                      <Input value={f.label} onChange={(e) => update(idx, { label: e.target.value })} disabled={!editable} />
                    </div>
                    <div>
                      <Label className="text-xs">Type</Label>
                      <Select value={f.type} onValueChange={(v) => update(idx, { type: v })} disabled={!editable}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {FIELD_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Group / section</Label>
                      <Input value={f.group || ""} onChange={(e) => update(idx, { group: e.target.value })} disabled={!editable} />
                    </div>
                    <div>
                      <Label className="text-xs">Prefill from Acuity</Label>
                      <Select value={f.prefill || "_none"} onValueChange={(v) => update(idx, { prefill: v === "_none" ? "" : v })} disabled={!editable}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {PREFILL_OPTIONS.map((p) => <SelectItem key={p.v || "none"} value={p.v || "_none"}>{p.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Help text / consent paragraph</Label>
                      <Textarea value={f.help || ""} onChange={(e) => update(idx, { help: e.target.value })} rows={2} disabled={!editable} />
                    </div>
                    {f.type === "file" && (
                      <div className="col-span-2">
                        <Label className="text-xs">
                          OCR pre-fill target (optional)
                        </Label>
                        <Select
                          value={f.ocr_target_field_id || "_none"}
                          onValueChange={(v) => update(idx, { ocr_target_field_id: v === "_none" ? "" : v })}
                          disabled={!editable}
                        >
                          <SelectTrigger data-testid={`template-ocr-target-${idx}`}>
                            <SelectValue placeholder="No auto-fill" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none">No auto-fill</SelectItem>
                            {/* Every text-like field on THIS form is a
                                candidate OCR target. We filter out the
                                file field itself (can't self-target)
                                and any other file fields. */}
                            {fields
                              .filter((g, gi) =>
                                gi !== idx
                                && ["text", "long-text", "email", "phone",
                                    "number", "date"].includes(g.type))
                              .map((g) => (
                                <SelectItem key={g.id} value={g.id}>
                                  {g.label || g.id}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        <p className="text-2xs text-zinc-500 mt-1">
                          When set, an OCR + LLM extraction runs on
                          upload and offers the client a one-tap pre-
                          fill suggestion for the selected field.
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <label className="flex items-center gap-2 text-xs cursor-pointer">
                      <input type="checkbox" checked={!!f.required} onChange={(e) => update(idx, { required: e.target.checked })} disabled={!editable} />
                      Required
                    </label>
                    {editable && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => move(idx, -1)} disabled={idx === 0}>↑</Button>
                        <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => move(idx, +1)} disabled={idx === fields.length - 1}>↓</Button>
                        <Button size="sm" variant="outline" className="h-7 px-2 text-red-700" onClick={() => remove(idx)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>
                </Card>
              ))}
              {fields.length === 0 && (
                <div className="text-sm text-zinc-500 py-2">No fields yet. Click &quot;Add field&quot;.</div>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)}>Cancel</Button>
          {editable && (
            <Button onClick={save} disabled={saving} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="template-save">
              {saving ? "Saving…" : "Save template"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
